import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, ChefHat } from "lucide-react";
import { SpiceBadge, TimeBadge } from "./badges";

export function CreatorsTile({ recipes = [] }) {
  const [i, setI] = useState(0);
  const n = recipes.length;
  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);

  useEffect(() => {
    setI(0);
  }, [recipes]);

  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n]);

  if (n === 0) return null;
  const r = recipes[i];

  return (
    <section className="mt-8" data-testid="creators-tile">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white px-3 py-1 text-xs font-black tracking-wide shrink-0">
            <ChefHat size={13} className="text-coral" /> Creators' Tile
          </span>
          <span className="text-sm text-ink-soft font-medium truncate">Our creators have cooked this too</span>
        </div>
        {n > 1 && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => go(-1)} data-testid="creators-tile-prev" aria-label="Previous creator recipe"
              className="grid place-items-center w-11 h-11 rounded-full border border-border bg-white hover:border-coral text-ink transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => go(1)} data-testid="creators-tile-next" aria-label="Next creator recipe"
              className="grid place-items-center w-11 h-11 rounded-full border border-border bg-white hover:border-coral text-ink transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      <Link key={r.id} to={`/recipe/${r.slug}`} data-testid={`creators-tile-card-${r.slug}`}
        className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] rounded-3xl bg-white border border-border shadow-soft hover:shadow-lift transition-all duration-300 overflow-hidden">
        <div className="relative h-44 sm:h-auto sm:min-h-[180px] overflow-hidden">
          <img src={r.thumbnail} alt={r.title} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-sm font-bold text-amber-700">
            <Star size={14} className="fill-gold text-gold" /> {r.rating_avg}
          </span>
        </div>
        <div className="p-5 sm:p-6 flex flex-col gap-2 justify-center">
          <h3 className="font-heading text-2xl font-black text-ink leading-tight">{r.title}</h3>
          <div className="flex flex-wrap gap-2">
            <SpiceBadge level={r.spice_level} />
            <TimeBadge minutes={r.cook_time} />
          </div>
          <p className="text-sm text-ink-soft">
            By <span className="font-bold text-ink">{r.creator?.display_name}</span> · {Number(r.rating_count || 0).toLocaleString()} ratings
          </p>
          <span className="mt-2 inline-flex items-center gap-1.5 font-bold text-coral group-hover:gap-2.5 transition-all">
            View recipe <ChevronRight size={17} />
          </span>
        </div>
      </Link>

      {n > 1 && (
        <div className="mt-3 flex justify-center gap-0.5" data-testid="creators-tile-dots">
          {recipes.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} data-testid={`creators-tile-dot-${idx}`} aria-label={`Go to recipe ${idx + 1}`}
              className="grid place-items-center w-10 h-10">
              <span className={`block h-2 rounded-full transition-all ${idx === i ? "w-6 bg-coral" : "w-2 bg-border hover:bg-ink-soft/40"}`} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
